import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { MatMenuModule } from "@angular/material/menu";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Subject, merge, takeUntil, tap } from "rxjs";
import { IAppDashboard, SessionService } from "core-client/api";
import { SharedModule } from "../shared/shared.module";
import {
  DashboardSelectionService,
  IDashboardSelectionView,
} from "./dashboard-selection.service";

@Component({
  selector: "lib-dashboard-select-menu",
  templateUrl: "./dashboard-select-menu.component.html",
  styleUrls: ["./dashboard-select-menu.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, MatMenuModule],
})
export class DashboardSelectMenuComponent implements OnInit, OnDestroy {
  protected standardDashboards: IAppDashboard[] = [];
  protected personalDashboards: IAppDashboard[] = [];
  protected activeDashboardId = "";
  protected blockedDashboardIds: string[] = [];
  protected canCreateStandard = false;
  protected canListStandard = false;
  protected canListPersonal = false;
  protected loading = true;

  /** Set by the header: only show the bottom divider when actions (New/Edit/Delete) follow the panel. */
  @Input() showSeparator = false;

  private readonly destroyed$ = new Subject<void>();
  /** Guards against a slow load overwriting the result of a newer one. */
  private loadVersion = 0;

  constructor(
    private readonly dashboardSelectionService: DashboardSelectionService,
    private readonly sessionService: SessionService,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // The panel is projected eagerly into the header's mat-menu, so it is instantiated once
    // with the header and stays alive for the whole session — its list would otherwise keep
    // showing the dashboards that existed back then. Both session streams re-read it:
    // the user carries the dashboard list (created/deleted elsewhere), the active dashboard
    // carries its own edits (a rename) and drives the per-dashboard permission flags.
    const activeDashboardChanges$ = this.sessionService.dashboard
      .observeActiveAppDashboard$()
      .pipe(
        tap((dashboard: IAppDashboard) => {
          this.activeDashboardId = dashboard._id;
        }),
      );

    merge(this.sessionService.user.observeUser$(), activeDashboardChanges$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        void this.reload();
      });
  }

  /** Never rejects: a failed load leaves the currently rendered list in place. */
  protected async reload(): Promise<void> {
    const version = ++this.loadVersion;
    try {
      const view = await this.dashboardSelectionService.loadView();
      if (version !== this.loadVersion) {
        return;
      }
      this.applyView(view);
    } catch (error) {
      console.error(
        "[DashboardSelectMenuComponent] loading the dashboard list failed",
        error,
      );
      if (version === this.loadVersion) {
        this.loading = false;
      }
    }
  }

  private applyView(view: IDashboardSelectionView | null): void {
    if (view) {
      this.standardDashboards = view.groups.standard;
      this.personalDashboards = view.groups.personal;
      this.activeDashboardId = view.activeDashboardId;
      this.blockedDashboardIds = view.blockedDashboardIds;
      this.canCreateStandard = view.canCreateStandard;
      this.canListStandard = view.canListStandard;
      this.canListPersonal = view.canListPersonal;
    } else {
      this.standardDashboards = [];
      this.personalDashboards = [];
      this.blockedDashboardIds = [];
    }
    this.loading = false;
  }

  protected isPublishable(dashboard: IAppDashboard): boolean {
    return (
      this.canCreateStandard &&
      dashboard.roleAssignable &&
      !dashboard.isPublished
    );
  }

  protected isBlocked(dashboard: IAppDashboard): boolean {
    return this.blockedDashboardIds.includes(dashboard._id);
  }

  // The row is a mat-menu-item, so the click bubbles to the menu panel and closes it automatically.
  protected async select(dashboard: IAppDashboard): Promise<void> {
    if (dashboard._id === this.activeDashboardId || this.isBlocked(dashboard)) {
      return;
    }
    try {
      const result = await this.dashboardSelectionService.activate(dashboard);
      if (result === "denied") {
        this.notify(
          this.translate.instant("CORE_PLUGIN.DASHBOARD.ACCESS_AUTHORIZATION_CHANGED"),
          "warn-snackbar",
          3000,
        );
        await this.reload();
        return;
      }
      this.notify(
        `${this.translate.instant("CORE_PLUGIN.DASHBOARD.DASHBOARD")} ${dashboard.name} ${this.translate.instant("CORE_PLUGIN.DASHBOARD.ACTIVATED")}`,
        "success-snackbar",
      );
    } catch {
      this.notify(
        this.translate.instant("CORE_PLUGIN.DASHBOARD.ACTIVATE_FAILED"),
        "error-snackbar",
      );
    }
  }

  // stopPropagation keeps the menu open so the user can keep selecting after publishing.
  protected async publish(
    dashboard: IAppDashboard,
    event: Event,
  ): Promise<void> {
    event.stopPropagation();
    try {
      const published = await this.dashboardSelectionService.publish(dashboard);
      this.notify(
        `${this.translate.instant("CORE_PLUGIN.DASHBOARD.DASHBOARD")} ${published.name} ${this.translate.instant("CORE_PLUGIN.DASHBOARD.PUBLISHED")}`,
        "success-snackbar",
      );
      await this.reload();
    } catch {
      this.notify(
        this.translate.instant("CORE_PLUGIN.DASHBOARD.PUBLISH_FAILED"),
        "error-snackbar",
      );
    }
  }

  private notify(message: string, panelClass: string, duration = 2000): void {
    this.snackBar.open(message, "", {
      duration,
      panelClass,
      verticalPosition: "top",
      horizontalPosition: "center",
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
